import { useMemo, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { WarningIcon } from '@phosphor-icons/react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { FieldError } from '@/components/ui/field-error';
import { FormDateField } from '@/components/ui/form-date-field';
import { FormNumberField } from '@/components/ui/form-number-field';
import { InvitationGroupsSelector } from '@/components/forms/InvitationGroupsSelector';
import { SearchBar } from '@/components/ui/search-bar';
import { UserListRow } from '@/components/UserListRow';
import type { EventFormInstance } from '@/lib/hooks/useEventForm';
import { useGroups } from '@/lib/hooks/useGroups';
import { resolveEventAudiencePreview } from '@/lib/services/event-targets';
import type {
  EventAudienceMatchReason,
  EventAudienceMemberRow,
  UserRole,
} from '@/lib/types/database';
import { cn } from '@/lib/utils';
import {
  getEventAudienceRoleLabel,
  getEventAudienceGroupsForRole,
} from '@/lib/utils/event-audience.utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getInitials } from '@/lib/utils/name.utils';

type RsvpAttendanceSectionProps = {
  form: EventFormInstance;
  submitAttempted?: boolean;
  validationErrors?: Record<string, string[]>;
};

function getReasonLabel(reason: EventAudienceMatchReason): string {
  return reason.kind === 'role'
    ? getEventAudienceRoleLabel(reason.role)
    : reason.display_name;
}

function AudienceRoleBadge({ role }: { role: UserRole }) {
  const tone = getGroupRoleTone(role);

  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-slate-550 px-2 py-1 text-small text-slate-200">
      <span
        className={cn('h-1.5 w-1.5 rounded-full', tone.radioDotClassName)}
      />
      {getEventAudienceRoleLabel(role)}
    </span>
  );
}

function AudienceMatchBadge({ reason }: { reason: EventAudienceMatchReason }) {
  const archived = reason.kind === 'group' && !!reason.archived_at;
  const role = reason.kind === 'role' ? reason.role : reason.parent_role;

  return (
    <span
      className={cn(
        'inline-flex max-w-[180px] items-center gap-1 truncate rounded-full border px-2 py-0.5 font-field-42 text-[10px]',
        archived
          ? 'border-amber-400/40 bg-amber-400/15 text-amber-300'
          : getGroupRoleTone(role).pillClassName
      )}
      title={
        archived
          ? `${getReasonLabel(reason)} (Archived)`
          : getReasonLabel(reason)
      }
    >
      <span className="truncate">{getReasonLabel(reason)}</span>
    </span>
  );
}

function AudiencePreviewMemberMeta({
  member,
}: {
  member: EventAudienceMemberRow;
}) {
  const groupMatchReasons = member.match_reasons.filter(
    (reason) => reason.kind === 'group'
  );

  return (
    <div className="space-y-2">
      {member.email && (
        <p className="truncate text-small text-slate-300">{member.email}</p>
      )}
      {groupMatchReasons.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {groupMatchReasons.map((reason) => (
            <AudienceMatchBadge
              key={`${reason.kind}-${getReasonLabel(reason)}`}
              reason={reason}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function RsvpAttendanceSection({
  form,
  submitAttempted = false,
  validationErrors = {},
}: RsvpAttendanceSectionProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSearch, setPreviewSearch] = useState('');
  const [drillRole, setDrillRole] = useState<UserRole | null>(null);
  const { data: groups = [], isLoading: isLoadingGroups } = useGroups(true);
  const audienceValues = useStore(form.store, (state) => ({
    roleTargets: state.values.invitationGroups ?? new Set<UserRole>(),
    groupTargetIds: state.values.eventGroupTargetIds ?? new Set<string>(),
  }));
  const roleTargetList = useMemo(
    () => Array.from(audienceValues.roleTargets),
    [audienceValues.roleTargets]
  );
  const groupTargetList = useMemo(
    () => Array.from(audienceValues.groupTargetIds),
    [audienceValues.groupTargetIds]
  );
  const hasTargets = roleTargetList.length > 0 || groupTargetList.length > 0;
  const previewQuery = useQuery({
    queryKey: [
      'event-audience-preview',
      roleTargetList.slice().sort(),
      groupTargetList.slice().sort(),
    ],
    queryFn: () =>
      resolveEventAudiencePreview({
        roleTargets: roleTargetList,
        groupTargetIds: groupTargetList,
      }),
    enabled: hasTargets,
    staleTime: 15 * 1000,
  });
  const drillRoleCountQuery = useQuery({
    queryKey: ['event-audience-preview', 'role-count', drillRole],
    queryFn: () => {
      if (!drillRole) {
        return Promise.resolve([]);
      }

      return resolveEventAudiencePreview({
        roleTargets: [drillRole],
        groupTargetIds: [],
      });
    },
    enabled: drillRole !== null,
    staleTime: 15 * 1000,
  });
  const previewMembers = previewQuery.data ?? [];
  const filteredPreviewMembers = previewMembers.filter((member) => {
    const query = previewSearch.trim().toLowerCase();

    if (!query) {
      return true;
    }

    return [member.full_name, member.email, member.role]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query);
  });

  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitle>RSVP & Attendance</CardTitle>
        <div className="h-[1px] bg-[#F9F8F8]/15 border-0" />
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <form.Field name="rsvpDeadline">
            {(field) => (
              <FormDateField
                field={field}
                label="RSVP Deadline"
                forceShowErrors={submitAttempted}
                externalErrors={validationErrors.rsvpDeadline}
              />
            )}
          </form.Field>

          <form.Field name="capacity">
            {(field) => (
              <FormNumberField
                field={field}
                label="Attendance Max"
                placeholder="25"
                min={1}
                forceShowErrors={submitAttempted}
                externalErrors={validationErrors.capacity}
              />
            )}
          </form.Field>
        </div>

        <form.Field name="invitationGroups">
          {(roleField) => (
            <form.Field name="eventGroupTargetIds">
              {(groupField) => {
                const selectedRoles =
                  roleField.state.value ?? new Set<UserRole>();
                const selectedGroups =
                  groupField.state.value ?? new Set<string>();
                const errors =
                  validationErrors.invitationGroups ||
                  roleField.state.meta.errors ||
                  [];
                const showErrors =
                  submitAttempted || roleField.state.meta.isTouched;

                const setRoleAll = (role: UserRole) => {
                  const nextRoles = new Set(selectedRoles);
                  const nextGroups = new Set(selectedGroups);
                  const roleGroups = getEventAudienceGroupsForRole(
                    role,
                    groups,
                    selectedGroups
                  );

                  nextRoles.delete(role);
                  roleGroups.forEach((group) => nextGroups.delete(group.id));
                  nextRoles.add(role);

                  roleField.handleChange(nextRoles);
                  groupField.handleChange(nextGroups);
                };

                const toggleGroup = (role: UserRole, groupId: string) => {
                  const nextRoles = new Set(selectedRoles);
                  const nextGroups = new Set(selectedGroups);

                  nextRoles.delete(role);

                  if (nextGroups.has(groupId)) {
                    nextGroups.delete(groupId);
                  } else {
                    nextGroups.add(groupId);
                  }

                  roleField.handleChange(nextRoles);
                  groupField.handleChange(nextGroups);
                };

                const removeRole = (role: UserRole) => {
                  const nextRoles = new Set(selectedRoles);
                  const nextGroups = new Set(selectedGroups);
                  const roleGroups = getEventAudienceGroupsForRole(
                    role,
                    groups,
                    selectedGroups
                  );

                  nextRoles.delete(role);
                  roleGroups.forEach((group) => nextGroups.delete(group.id));

                  roleField.handleChange(nextRoles);
                  groupField.handleChange(nextGroups);
                };

                return (
                  <div className="space-y-4">
                    <InvitationGroupsSelector
                      selectedRoles={selectedRoles}
                      selectedGroupIds={selectedGroups}
                      groups={groups}
                      isLoadingGroups={isLoadingGroups}
                      audienceMemberCount={previewMembers.length}
                      isAudienceLoading={previewQuery.isLoading}
                      hasTargets={hasTargets}
                      isPreviewDisabled={
                        !hasTargets ||
                        previewQuery.isLoading ||
                        previewQuery.isError
                      }
                      drillRole={drillRole}
                      drillRoleMemberCount={{
                        memberCount: drillRoleCountQuery.data
                          ? drillRoleCountQuery.data.length
                          : null,
                        isLoading: drillRoleCountQuery.isLoading,
                        isError: drillRoleCountQuery.isError,
                      }}
                      onDrillRoleChange={setDrillRole}
                      onPreview={() => setPreviewOpen(true)}
                      onRoleAdd={setRoleAll}
                      onRoleSetAll={setRoleAll}
                      onGroupToggle={toggleGroup}
                      onRoleRemove={removeRole}
                    />

                    {showErrors && <FieldError errors={errors} />}

                    {previewQuery.isError && (
                      <div className="rounded-md border border-red-400/40 bg-red-400/10 p-3 text-small text-red-200">
                        Audience preview could not be calculated.
                      </div>
                    )}

                    {hasTargets &&
                      !previewQuery.isLoading &&
                      !previewQuery.isError &&
                      previewMembers.length === 0 && (
                        <div className="flex gap-2 rounded-md border border-yellow-300/40 bg-yellow-300/10 p-3 text-small text-yellow-100">
                          <WarningIcon size={18} className="shrink-0" />
                          Selected targets currently resolve to zero members.
                        </div>
                      )}

                    <Drawer open={previewOpen} onOpenChange={setPreviewOpen}>
                      <DrawerContent className="max-h-[85vh]">
                        <DrawerHeader>
                          <DrawerTitle>Audience Preview</DrawerTitle>
                        </DrawerHeader>
                        <div className="space-y-4 overflow-y-auto pb-4">
                          <SearchBar
                            value={previewSearch}
                            onChange={setPreviewSearch}
                            placeholder="Search audience"
                          />
                          {filteredPreviewMembers.length > 0 ? (
                            <ul className="overflow-hidden rounded-md border border-slate-550 bg-slate-600">
                              {filteredPreviewMembers.map((member, index) => {
                                const memberName =
                                  member.full_name ||
                                  member.email ||
                                  'Unknown User';

                                return (
                                  <li
                                    key={member.user_id}
                                    className={cn(
                                      index > 0 && 'border-t border-slate-550'
                                    )}
                                  >
                                    <UserListRow
                                      name={memberName}
                                      initials={getInitials(memberName)}
                                      className="px-3 py-3"
                                      trailingBadge={
                                        member.role ? (
                                          <AudienceRoleBadge
                                            role={member.role}
                                          />
                                        ) : undefined
                                      }
                                      belowName={
                                        <AudiencePreviewMemberMeta
                                          member={member}
                                        />
                                      }
                                    />
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="py-6 text-center text-body text-slate-300">
                              No matching members.
                            </p>
                          )}
                        </div>
                      </DrawerContent>
                    </Drawer>
                  </div>
                );
              }}
            </form.Field>
          )}
        </form.Field>
      </CardContent>
    </Card>
  );
}
